<template>
  <div class="home-page">
    <!-- Back to top button -->
    <transition name="fade">
      <button
        v-if="showBackToTop"
        class="back-to-top"
        type="button"
        @click="scrollToTop"
        aria-label="Back to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      </button>
    </transition>
    <section class="hero" id="hero">
      <div class="container hero-content">
        <div class="hero-copy">
          <div class="badge">
            <span>GLOBAL TOP-SELLING PRODUCTS</span>
          </div>
          <h1>Global Top-Selling Products One-Stop Sourcing & Supply Platform</h1>
          <p>Providing end-to-end services from product selection planning, flexible supply to global delivery, enabling quality products to reach global consumers quickly.</p>
          <div class="hero-actions">
            <router-link to="/products" class="cta-button">View All Products</router-link>
            <button type="button" class="secondary-button" @click="scrollToSection('support')">
              Get Industry Solutions
            </button>
          </div>
          <div class="hero-stats">
            <div class="stat-item">
              <strong>120+</strong>
              <span>Best-Selling Models</span>
            </div>
            <div class="stat-item">
              <strong>60+</strong>
              <span>Global Shipping Countries</span>
            </div>
            <div class="stat-item">
              <strong>24h</strong>
              <span>Dedicated Support Response</span>
            </div>
          </div>
        </div>
        <div class="hero-banner">
          <div
            v-if="mainFeature"
            class="banner-card primary"
            role="button"
            tabindex="0"
            @click="goToDetail(mainFeature.prodId)"
            @keyup.enter="goToDetail(mainFeature.prodId)"
          >
            <div class="banner-media">
              <img :src="mainFeatureImage" :alt="mainFeature?.prodTitle || 'Best Seller'" />
              <div class="banner-meta">
                <div class="banner-price" v-if="mainFeature?.prodPrice">${{ mainFeature.prodPrice }}</div>
                <div class="banner-reviews" v-if="mainFeature?.prodReview">{{ mainFeature.prodReview }}+ Reviews</div>
              </div>
            </div>
            <div class="banner-info">
              <h3>{{ mainFeature?.prodTitle || 'Best Seller' }}</h3>
            </div>
          </div>
          <div class="banner-card secondary">
            <div class="card-item" v-for="item in secondaryFeatures" :key="item.prodId" @click="goToDetail(item.prodId)">
              <img :src="getFirstImage(item.prodImgurl)" :alt="item.prodTitle" />
              <div class="info">
                <h4>{{ item.prodTitle.split(',')[0] }}</h4>
                <span class="price" v-if="item.prodPrice">${{ item.prodPrice }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="category-highlight" id="category-highlight">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Core Product Selection</h2>
          <p class="section-subtitle">Curated star products for various scenarios, quickly building a high-conversion product pool.</p>
        </div>
        <div class="category-grid">
          <div
            class="category-card"
            v-for="category in categoryCards"
            :key="category.title"
            role="button"
            tabindex="0"
            @click="category.action && category.action()"
            @keyup.enter="category.action && category.action()"
          >
            <div class="card-media">
              <img :src="category.image" :alt="category.title" />
            </div>
            <div class="card-body">
              <h3>{{ category.title }}</h3>
              <div class="category-meta">
                <span class="price" v-if="category.price">${{ category.price }}</span>
                <div class="rating" v-if="category.review">
                  ★★★★★ <span>({{ category.review }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="product-showcase" id="new-arrivals">
      <div class="container">
        <div class="section-heading">
          <div class="badge">NEW ARRIVALS</div>
          <h2 class="section-title">New Arrivals · Launch Special</h2>
          <p class="section-subtitle">Focusing on high-rated new products on the platform, with comprehensive upgrades from feature highlights to packaging experience.</p>
        </div>
        <div class="product-grid">
          <div class="product-card" v-for="product in newArrivals" :key="product.prodId" @click="goToDetail(product.prodId)" role="button" tabindex="0" @keyup.enter="goToDetail(product.prodId)">
            <div class="product-thumb">
              <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
              <span class="tag">New</span>
            </div>
            <div class="product-info">
              <h3>{{ product.prodTitle.split(',')[0] }}</h3>
              <div class="product-meta">
                <span class="price" v-if="product.prodPrice">${{ product.prodPrice }}</span>
                <div class="rating" v-if="product.prodReview">
                  ★★★★★ <span>({{ product.prodReview }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="section-action">
          <router-link to="/products" class="cta-button">Browse More New Arrivals</router-link>
        </div>
      </div>
    </section>

    <section class="best-selling" id="best-selling">
      <div class="container">
        <div class="section-heading">
          <div class="badge">TOP SELLERS</div>
          <h2 class="section-title">Best Sellers · Customer Favorites</h2>
          <p class="section-subtitle">Based on real sales and user feedback, carefully selected best-selling items to help maintain hot sales.</p>
        </div>
        <div class="best-grid">
          <div class="featured-card" v-if="bestSelling[0]" @click="goToDetail(bestSelling[0].prodId)">
            <img :src="bestSelling[0].prodImgurl?.split(',')[0]" :alt="bestSelling[0].prodTitle" />
            <div class="overlay">
              <h3>{{ bestSelling[0].prodTitle.split(',')[0] }}</h3>
              <div class="meta">
                <span v-if="bestSelling[0].prodPrice">${{ bestSelling[0].prodPrice }}</span>
                <span class="reviews" v-if="bestSelling[0].prodReview">{{ bestSelling[0].prodReview }}+ Reviews</span>
              </div>
            </div>
          </div>
          <div class="best-list">
            <div class="best-item" v-for="product in bestSelling.slice(1, 5)" :key="product.prodId" @click="goToDetail(product.prodId)">
              <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
              <div class="info">
                <h4>{{ product.prodTitle.split(',')[0] }}</h4>
                <div class="meta">
                  <span v-if="product.prodPrice">${{ product.prodPrice }}</span>
                  <span class="reviews" v-if="product.prodReview">{{ product.prodReview }}+ Reviews</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="accessories" id="collections">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Bundle Sets · Scenario-Based Recommendations</h2>
          <p class="section-subtitle">Package core products with accessories to quickly replicate a complete experience.</p>
        </div>
        <div class="accessory-grid">
          <div
            class="accessory-card"
            v-for="product in collectionProducts"
            :key="product.prodId"
            role="button"
            tabindex="0"
            @click="goToDetail(product.prodId)"
            @keyup.enter="goToDetail(product.prodId)"
          >
            <div class="thumb">
              <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
            </div>
            <div class="body">
              <h3>{{ product.prodTitle.split(',')[0] }}</h3>
              <div class="accessory-meta">
                <span class="price" v-if="product.prodPrice">${{ product.prodPrice }}</span>
                <div class="rating" v-if="product.prodReview">
                  ★★★★★ <span>({{ product.prodReview }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="blog-section" id="blog">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Guides & Information</h2>
          <p class="section-subtitle">Read installation tutorials, usage tips and case studies to quickly create high-converting product selling points.</p>
        </div>
        <div class="blog-grid">
          <article class="blog-card" v-for="article in blogPosts" :key="article.title">
            <div class="badge">{{ article.tag }}</div>
            <h3>{{ article.title }}</h3>
            <p>{{ article.summary }}</p>
            <a :href="article.link" target="_blank" rel="noopener" class="link-button">
              Read More
            </a>
          </article>
        </div>
      </div>
    </section>

    <section class="support-section" id="support">
      <div class="container support-grid">
        <div class="support-card">
          <h3>Enterprise Cooperation</h3>
          <p>For bulk purchasing, ODM customization, and distribution partnerships, please contact our business manager. We provide solutions and quotes within 24 hours.</p>
          <router-link to="/contact" class="link-button">Contact Business Team</router-link>
        </div>
        <div class="support-card">
          <h3>Resource Download Center</h3>
          <p>Product specifications, high-resolution materials, certification documents and manuals are continuously updated for quick listing and promotion.</p>
          <a href="#" class="link-button">Go to Download</a>
        </div>
        <div class="support-card">
          <h3>After-Sales & Customer Support</h3>
          <p>Providing one-stop services including product consultation, usage guides, after-sales maintenance and warranty information.</p>
          <a class="link-button" @click="scrollToSection('faq')">View FAQ</a>
        </div>
      </div>
    </section>

    <section class="faq-section" id="faq">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Frequently Asked Questions</h2>
          <p class="section-subtitle">Find answers about purchasing, payment, resources, logistics and after-sales here.</p>
        </div>
        <div class="faq-grid">
          <div class="faq-item" v-for="faq in faqs" :key="faq.q">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'CompanyHome',
  data() {
    return {
      products: [],
      showBackToTop: false,
      faqs: [
        {
          q: 'How to proceed with bulk purchasing and customization?',
          a: 'Contact our business team, specify your target market, purchase quantity and packaging requirements. We can provide OEM/ODM services and exclusive quotes.'
        },
        {
          q: 'What payment and logistics methods are supported?',
          a: 'We support various payment methods including bank transfer and PayPal. International shipping available via DHL, UPS, FedEx, and SF Express International.'
        },
        {
          q: 'Can you provide product materials and marketing assets?',
          a: 'We can provide specification sheets, high-resolution images, short videos and certification documents, with multi-language localization support for quick listing.'
        },
        {
          q: 'What is the warranty and after-sales policy?',
          a: 'Complete units come with 12 months limited warranty, accessories with 6 months. Mail-in repair and replacement supported. See after-sales terms in payment guide for details.'
        }
      ]
    }
  },
  computed: {
    mainFeature() {
      return this.products[0] || null
    },
    mainFeatureImage() {
      return this.getFirstImage(this.mainFeature?.prodImgurl) || 'https://via.placeholder.com/420x480?text=Top+Product'
    },
    primaryProductName() {
      return this.mainFeature ? this.cleanTitle(this.mainFeature.prodTitle) : 'Best Seller'
    },
    secondaryFeatures() {
      return this.products.slice(1, 3)
    },
    newArrivals() {
      return this.products
        .slice()
        .sort((a, b) => this.parseTime(b.createTime) - this.parseTime(a.createTime))
        .slice(0, 8)
    },
    bestSelling() {
      return this.products
        .slice()
        .sort((a, b) => this.parseNumber(b.prodReview) - this.parseNumber(a.prodReview))
        .slice(0, 5)
    },
    collectionProducts() {
      return this.products
        .slice()
        .sort((a, b) => this.parseNumber(b.prodPrice) - this.parseNumber(a.prodPrice))
        .slice(0, 4)
    },
    categoryCards() {
      const cards = this.products.slice(0, 3).map(product => ({
        title: this.cleanTitle(product.prodTitle),
        desc: this.formatContent(product.prodContent),
        features: this.extractHighlights(product),
        image: this.getFirstImage(product.prodImgurl) || 'https://via.placeholder.com/420x320?text=Product',
        price: product.prodPrice,
        review: product.prodReview,
        action: () => this.goToDetail(product.prodId),
        cta: 'View Details'
      }))
      if (!cards.length) {
        cards.push({
          title: 'Best-Selling Products Selection',
          desc: 'Real-time synchronization with overseas platform best-seller rankings, quickly completing hot product structure.',
          features: ['Stable Supply Chain', 'Complete Specifications', 'Fast Shipping'],
          image: 'https://via.placeholder.com/420x320?text=Collection',
          action: () => this.goToProducts(),
          cta: 'Browse All Products'
        })
      }
      return cards
    },
    blogPosts() {
      return [
        {
          title: 'How to Quickly Evaluate Cross-Border Best-Selling Potential',
          summary: 'Organize key points of market research, competitor analysis and cost accounting to make product selection more efficient.',
          link: '#',
          tag: 'Selection Guide'
        },
        {
          title: 'How Supply Chain Improves Fulfillment Efficiency',
          summary: 'From inventory planning and warehouse layout to international logistics, organize full-chain collaboration strategies.',
          link: '#',
          tag: 'Supply Chain Insights'
        },
        {
          title: 'What Materials Are Needed Before New Product Launch?',
          summary: 'Complete breakdown of photography, product pages and customer service scripts to help teams efficiently launch new products.',
          link: '#',
          tag: 'Operations Tips'
        }
      ]
    }
  },
  mounted() {
    this.fetchProducts()
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    fetchProducts() {
      const params = {
        prodDomain: window.location.hostname.split('.')[1]
      }
      this.$axios.post('/asin/list', params).then(response => {
        if (response.data.code === 0) {
          this.products = response.data.data || []
        }
      }).catch(error => {
        console.error('Error fetching products:', error)
      })
    },
    goToDetail(prodId) {
      if (!prodId) return
      const url = this.$router.resolve(`/product/${prodId}`).href
      window.open(url, '_blank')
    },
    goToProducts() {
      this.$router.push('/products')
    },
    formatContent(content) {
      if (!content) return 'Best-selling item, learn more about highlights and experience.'
      const text = content.replace(/<br\s*\/?>/gi, ' ').replace(/[,，;]/g, ' ')
      return text.split(' ').slice(0, 12).join(' ')
    },
    extractHighlights(product) {
      if (!product?.prodContent) return ['Quality Selection', 'Great Reviews', 'Best Value']
      const parts = product.prodContent.split('<br/>')
      return parts.filter(Boolean).slice(0, 3).map(part => part.split(/[，,]/)[0])
    },
    extractCollectionFeatures(product) {
      if (!product?.prodContent) return ['Multi-Item Bundle', 'Complete Set']
      return product.prodContent.split('<br/>').slice(0, 3).map(line => line.split(/[，,]/)[0])
    },
    cleanTitle(title = '') {
      if (!title) return ''
      return title.split(/[,-]/)[0].trim()
    },
    scrollToSection(sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    parseTime(value) {
      if (!value) return 0
      const time = new Date(value).getTime()
      return Number.isNaN(time) ? 0 : time
    },
    parseNumber(value) {
      if (!value) return 0
      if (typeof value === 'number') return value
      const normalized = String(value).replace(/[^\d.]/g, '')
      const num = Number(normalized)
      return Number.isNaN(num) ? 0 : num
    },
    getFirstImage(imgurl) {
      return imgurl?.split(',')[0] || ''
    },
    handleScroll() {
      this.showBackToTop = window.scrollY > 300
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.home-page {
  display: flex;
  flex-direction: column;
  gap: 80px;
}

.hero {
  padding: calc(80px + var(--header-offset-desktop)) 0 100px;
  margin-top: calc(-1 * var(--header-offset-desktop));
  background: linear-gradient(135deg, rgba(11, 31, 59, 0.92) 0%, rgba(0, 113, 227, 0.8) 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1), transparent 55%);
  pointer-events: none;
}

.hero-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  align-items: center;
  gap: 56px;
  position: relative;
  z-index: 1;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.hero-copy h1 {
  font-size: 3rem;
  line-height: 1.2;
  font-weight: 700;
}

.hero-copy p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
}

.hero-actions {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  align-items: center;
}

.hero-actions .secondary-button {
  border: 2px solid rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 12px 28px;
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-base);
  backdrop-filter: blur(6px);
}

.hero-actions .secondary-button:hover {
  background: #fff;
  color: var(--brand-accent);
  border-color: transparent;
  box-shadow: var(--shadow-sm);
}

.hero-stats {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
}

.stat-item strong {
  font-size: 2rem;
  font-weight: 700;
}

.stat-item span {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.hero-banner {
  display: grid;
  gap: 20px;
}

.banner-card {
  border-radius: var(--radius-xl);
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: var(--shadow-md);
}

.banner-card.primary {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: center;
}

.banner-card.primary {
  position: relative;
  cursor: pointer;
}

.banner-card.primary::after {
  content: 'Click to View Details';
  position: absolute;
  left: 24px;
  bottom: 24px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  opacity: 0;
  transform: translateY(8px);
  transition: var(--transition-base);
  pointer-events: none;
}

.banner-card.primary:hover::after,
.banner-card.primary:focus-visible::after {
  opacity: 1;
  transform: translateY(0);
}

.banner-card.primary .banner-media {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 24px;
}

.banner-card.primary img {
  width: 100%;
  height: auto;
  max-height: 280px;
  object-fit: contain;
}

.banner-card.primary .banner-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
}

.banner-card.primary .banner-price {
  color: #ffd700;
  font-weight: 700;
  font-size: 1.1rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.banner-card.primary .banner-reviews {
  color: #ffd700;
  font-weight: 600;
  font-size: 0.9rem;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5), 0 0 8px rgba(255, 215, 0, 0.4);
}

.banner-card.primary .banner-info {
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.banner-card.primary h3 {
  font-size: 1.4rem;
  font-weight: 700;
}

.banner-card.primary p {
  color: rgba(255, 255, 255, 0.8);
}

.banner-card.secondary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  padding: 20px;
}

.banner-card.secondary .card-item {
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.12);
  padding: 18px;
  display: flex;
  gap: 16px;
  align-items: center;
  cursor: pointer;
  transition: var(--transition-base);
}

.banner-card.secondary .card-item:hover {
  transform: translateY(-4px);
}

.banner-card.secondary img {
  width: 92px;
  height: 92px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
}

.banner-card.secondary .info h4 {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.banner-card.secondary .info .price {
  color: #ffd700;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.section-heading {
  text-align: center;
  margin-bottom: 36px;
}

.heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 28px;
}

.category-card {
  display: grid;
  gap: 18px;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-base);
}

.category-card:hover,
.category-card:focus-visible {
  box-shadow: var(--shadow-md);
  transform: translateY(-4px);
}

.category-card .card-media {
  width: 100%;
  height: 200px;
  background: #f4f6fb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.category-card .card-media img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.category-card .card-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  justify-content: space-between;
}

.category-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
  color: var(--text-secondary);
}

.category-card ul li::before {
  content: '•';
  margin-right: 8px;
  color: var(--brand-accent);
}

.category-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  color: var(--text-secondary);
}

.category-meta .price {
  font-weight: 700;
  color: #dc2626;
}

.category-meta .rating {
  color: #f97316;
}

.category-meta .rating span {
  font-size: 0.85rem;
  margin-left: 6px;
  color: #f97316;
}

.product-showcase,
.best-selling,
.accessories,
.blog-section,
.support-section,
.faq-section {
  scroll-margin-top: 120px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-top: 28px;
}

.product-card {
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: var(--transition-base);
}

.product-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-md);
}

.product-thumb {
  position: relative;
  background: #f4f6fb;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 220px;
  padding: 18px;
}

.product-thumb img {
  max-height: 100%;
  width: auto;
}

.product-thumb .tag {
  position: absolute;
  top: 16px;
  left: 16px;
  background: var(--brand-accent);
  color: #fff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.product-info {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  justify-content: space-between;
}

.product-info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.product-info p {
  color: var(--text-secondary);
  line-height: 1.6;
}

.product-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  color: var(--text-secondary);
}

.product-meta .price {
  font-weight: 700;
  color: #dc2626;
}

.product-meta .rating {
  color: #f97316;
}

.product-meta .rating span {
  font-size: 0.85rem;
  margin-left: 6px;
  color: #f97316;
}

.section-action {
  margin-top: 32px;
  text-align: center;
}

.best-grid {
  display: grid;
  grid-template-columns: minmax(280px, 1.2fr) minmax(280px, 1fr);
  gap: 24px;
}

.featured-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
  min-height: 420px;
  cursor: pointer;
}

.featured-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.featured-card:hover img {
  transform: scale(1.05);
}

.featured-card .overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(11, 31, 59, 0.2) 0%, rgba(11, 31, 59, 0.85) 100%);
  color: #fff;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 14px;
}

.featured-card .overlay .meta {
  display: flex;
  gap: 16px;
  font-size: 0.9rem;
  color: #fff;
  margin-bottom: 4px;
}

.featured-card .overlay .meta > span:first-child {
  color: #dc2626;
  font-weight: 700;
}

.featured-card .overlay .meta .reviews {
  color: #f97316;
  font-weight: 600;
}

.featured-card .labels {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.featured-card .labels span {
  background: rgba(255, 255, 255, 0.16);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
}

.best-list {
  display: grid;
  gap: 18px;
}

.best-item {
  display: flex;
  gap: 16px;
  background: #fff;
  padding: 18px;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: var(--transition-base);
}

.best-item:hover {
  transform: translateX(6px);
  box-shadow: var(--shadow-sm);
}

.best-item img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  background: #f5f7fb;
  border-radius: var(--radius-md);
}

.best-item .info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.best-item .info h4 {
  font-size: 1rem;
  font-weight: 600;
}

.best-item .meta {
  display: flex;
  gap: 16px;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.best-item .meta > span:first-child {
  color: #dc2626;
  font-weight: 700;
}

.best-item .reviews {
  color: #f97316;
  font-weight: 600;
}

.accessory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 22px;
}

.accessory-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-base);
}

.accessory-card:hover,
.accessory-card:focus-visible {
  transform: translateY(-6px);
  box-shadow: var(--shadow-md);
}

.accessory-card .thumb {
  background: #f4f6fb;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.accessory-card img {
  max-height: 100%;
  width: auto;
}

.accessory-card .body {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  justify-content: space-between;
}

.accessory-card ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
  color: var(--text-secondary);
}

.accessory-card ul li::before {
  content: '—';
  margin-right: 8px;
  color: var(--brand-accent);
}

.accessory-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  color: var(--text-secondary);
}

.accessory-meta .price {
  font-weight: 700;
  color: #dc2626;
}

.accessory-meta .rating {
  color: #f97316;
}

.accessory-meta .rating span {
  font-size: 0.85rem;
  margin-left: 6px;
  color: #f97316;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
}

.blog-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: var(--shadow-sm);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.blog-card h3 {
  font-size: 1.2rem;
  font-weight: 600;
}

.blog-card p {
  color: var(--text-secondary);
}

.support-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 22px;
  margin-top: 32px;
}

.faq-item {
  background: #fff;
  border-radius: var(--radius-lg);
  padding: 22px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: var(--shadow-sm);
}

.faq-item h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.faq-item p {
  color: var(--text-secondary);
  line-height: 1.7;
}

@media (max-width: 1024px) {
  .hero-copy h1 {
    font-size: 2.4rem;
  }

  .hero {
    padding-top: calc(64px + var(--header-offset-tablet));
    margin-top: calc(-1 * var(--header-offset-tablet));
  }

  .banner-card.primary {
    grid-template-columns: 1fr;
  }

  .banner-card.secondary {
    grid-template-columns: 1fr;
  }

  .best-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .home-page {
    gap: 60px;
  }

  .hero {
    padding-top: calc(48px + var(--header-offset-mobile));
    margin-top: calc(-1 * var(--header-offset-mobile));
    padding-bottom: 64px;
  }

  .hero-copy h1 {
    font-size: 2rem;
  }

  .hero-stats {
    gap: 18px;
  }

  .product-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .link-button {
    justify-content: center;
  }
}

/* Back to top button */
.back-to-top {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #dc2626;
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: all 0.3s ease;
}

.back-to-top:hover {
  background: #b91c1c;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
}

.back-to-top:active {
  transform: translateY(0);
}

.back-to-top svg {
  width: 20px;
  height: 20px;
  display: block;
  margin: 0 auto;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .back-to-top {
    bottom: 24px;
    right: 24px;
    width: 44px;
    height: 44px;
  }

  .back-to-top svg {
    width: 18px;
    height: 18px;
  }
}
</style>
